import '../global.less';
import './RichEditor.scss';
import {
    CompositeDecorator,
    DraftHandleValue,
    Editor,
    EditorState,
    Modifier,
    RichUtils,
    SelectionState,
    getDefaultKeyBinding,
} from 'draft-js';
import { EditorContextProviders } from './context/EditorContextProviders';
import { KeyCommands, RichEditorVariable, ToolbarButtons } from './types';
import { KeyHandlerContext } from './context/KeyHandlerContext';
import { LinkDialog } from './LinkDialog';
import { Toolbar } from './Toolbar';
import { VariableSelection } from './VariableSelection';
import {
    getCurrentLinkData,
    getTextTriggerRange,
    handlePaste,
    handleTextInput,
    replaceWithLink,
    replaceWithVariable,
} from './utils';
import { getLinkEntityDecorator } from './decorators/linkEntityDecorator';
import { getVariableEntityDecorator } from './decorators/variableEntityDecorator';
import { getVariableSelectionDecorator } from './decorators/variableSelectionDecorator';
import React, { RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

interface RichEditorProps {
    variables: RichEditorVariable[];
    className?: string;
    readOnly?: boolean;
    richText?: boolean;
    editorState: EditorState;
    editorRef?: RefObject<Editor>;
    onChange: (editorState: EditorState) => void;
    singleLine?: boolean;
}

const RichEditorCore = (props: RichEditorProps) => {
    const { className, readOnly, editorState, onChange, richText, variables, editorRef, singleLine } = props;
    const { executeCommand } = useContext(KeyHandlerContext);

    const showToolbar = richText && !singleLine && !readOnly;
    const supportsRichText = richText && !singleLine;

    const localRef = useRef<Editor>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const variableOptionsRef = useRef<HTMLDivElement>(null);

    const ref = editorRef ?? localRef;

    const [popoverPortal, setPopoverPortal] = useState<HTMLSpanElement | undefined>();
    const updatePopoverPortal = useCallback((ref: HTMLSpanElement) => {
        setPopoverPortal(ref);
    }, []);

    const focus = useRef<boolean>(false);

    const [variableSelectionOpen, setVariableSelectionOpen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkData, setLinkData] = useState<{ text: string; url: string }>();

    const triggerRange = getTextTriggerRange(editorState);
    const searchText = triggerRange?.text;
    const rangeStart = triggerRange?.start ?? 0;
    const rangeEnd = triggerRange?.end ?? 1;

    useEffect(() => {
        setVariableSelectionOpen(searchText !== undefined);
        setTimeout(() => variableOptionsRef.current?.focus(), 1);
    }, [searchText]);

    const onBlur = useCallback(() => {
        focus.current = false;
    }, []);

    const onFocus = useCallback(() => {
        focus.current = true;
    }, []);

    const onSelectVariable = useCallback(
        (variable: RichEditorVariable) => {
            setVariableSelectionOpen(false);
            onChange(replaceWithVariable(editorState, variable, { start: rangeStart, end: rangeEnd }));
        },
        [editorState, onChange, rangeEnd, rangeStart]
    );

    const onVisibleChange = useCallback((visible) => {
        setVariableSelectionOpen(visible);
    }, []);

    const toggleInlineStyle = useCallback(
        (style: 'BOLD' | 'ITALIC' | 'UNDERLINE') => {
            onChange(RichUtils.toggleInlineStyle(editorState, style));
        },
        [editorState, onChange]
    );

    const toggleBlockType = useCallback(
        (blockType: 'unordered-list-item' | 'ordered-list-item') => {
            onChange(RichUtils.toggleBlockType(editorState, blockType));
        },
        [editorState, onChange]
    );

    const openLinkDialog = useCallback(() => {
        const { text, url } = getCurrentLinkData(editorState);
        setLinkData({ text: text, url: url });
        setLinkDialogOpen(true);
    }, [editorState, setLinkData]);

    const executeToolbarCommand = useCallback(
        (command: string) => {
            switch (command) {
                case KeyCommands.ToolbarBold:
                    toggleInlineStyle('BOLD');
                    break;
                case KeyCommands.ToolbarItalic:
                    toggleInlineStyle('ITALIC');
                    break;
                case KeyCommands.ToolbarUnderline:
                    toggleInlineStyle('UNDERLINE');
                    break;
                case KeyCommands.ToolbarBulletedList:
                    toggleBlockType('unordered-list-item');
                    break;
                case KeyCommands.ToolbarNumberedList:
                    toggleBlockType('ordered-list-item');
                    break;
                case KeyCommands.ToolbarLink:
                    openLinkDialog();
                    break;
                default:
                    break;
            }
        },
        [toggleInlineStyle, toggleBlockType, openLinkDialog]
    );

    const handleKeyCommand = useCallback(
        (command: string, editorState: EditorState) => {
            if (command === KeyCommands.AutoCompleteClose) {
                setVariableSelectionOpen(false);
                return 'handled';
            }

            if (supportsRichText) {
                const newState = RichUtils.handleKeyCommand(editorState, command);

                if (newState) {
                    onChange(newState);
                    return 'handled';
                }
            }

            return executeCommand(command);
        },
        [onChange, supportsRichText, executeCommand]
    );

    const keyBindingFn = useCallback(
        (e: React.KeyboardEvent): string | null => {
            if (variableSelectionOpen && ['Enter', 'Escape', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                switch (e.key) {
                    case 'Enter':
                        return KeyCommands.AutoCompleteSelect;
                    case 'Escape':
                        return KeyCommands.AutoCompleteClose;
                    case 'ArrowDown':
                        return KeyCommands.AutoCompleteNext;
                    case 'ArrowUp':
                        return KeyCommands.AutoCompletePrevious;
                }
            }

            if (e.key === 'Enter' && singleLine) {
                return 'handled';
            }

            return getDefaultKeyBinding(e);
        },
        [variableSelectionOpen, singleLine]
    );

    const handleBeforeInput = useCallback(
        (text: string, editorState: EditorState): DraftHandleValue => {
            const newState = handleTextInput(text, editorState, variables);

            if (newState.changed) {
                onChange(newState.editorState);
                return 'handled';
            }

            return 'not-handled';
        },
        [onChange, variables]
    );

    const handlePastedText = useCallback(
        (text: string, html: string | undefined, editorState: EditorState): DraftHandleValue => {
            const newState = handlePaste(text ?? html, editorState, variables, singleLine);

            if (newState.changed) {
                onChange(newState.editorState);
                return 'handled';
            }

            return 'not-handled';
        },
        [onChange, variables, singleLine]
    );

    const unlink = useCallback(
        (blockKey, start: number, end: number) => {
            let content = editorState.getCurrentContent();
            content = Modifier.applyEntity(
                content,
                new SelectionState({
                    focusKey: blockKey,
                    anchorKey: blockKey,
                    focusOffset: end,
                    anchorOffset: start,
                }),
                null
            );
            onChange(EditorState.push(editorState, content, 'apply-entity'));
        },
        [editorState, onChange]
    );

    const decoratedEditorState = EditorState.set(editorState, {
        decorator: new CompositeDecorator([
            getVariableSelectionDecorator({ variables, ref: updatePopoverPortal, triggerRange }),
            getVariableEntityDecorator({ variables, focused: focus.current }),
            getLinkEntityDecorator({ unlink }),
        ]),
    });

    const options = variables.filter((variable) => {
        return (variable.displayText?.toLowerCase() ?? '-1').indexOf(searchText?.toLowerCase() ?? '') > -1;
    });

    const setFocus = useCallback(() => {
        ref.current?.focus();
    }, [ref]);

    const linkButtonRef = useRef<HTMLDivElement>(null);
    const toolbarButtonRefs = { [ToolbarButtons.LINK]: linkButtonRef };

    const onSaveLink = useCallback(
        (link) => {
            setLinkDialogOpen(false);
            const newState = replaceWithLink(editorState, link.text, link.url);
            onChange(newState);
        },
        [editorState, onChange]
    );

    const onLinkDialogVisibilityChange = useCallback((visible: boolean) => {
        if (!visible) {
            setLinkDialogOpen(false);
        }
    }, []);

    return (
        <div
            className={classnames('rain-editor', className, singleLine && 'single-line', readOnly && 'read-only')}
            ref={containerRef}
            onClick={setFocus}
            data-testid="rich-editor-container">
            {showToolbar && (
                <Toolbar
                    editorRef={ref}
                    buttonRefs={toolbarButtonRefs}
                    executeCommand={executeToolbarCommand}
                    editorState={editorState}
                />
            )}
            <Editor
                ref={ref}
                onFocus={onFocus}
                onBlur={onBlur}
                editorState={decoratedEditorState}
                onChange={onChange}
                readOnly={linkDialogOpen || readOnly}
                handleKeyCommand={handleKeyCommand}
                keyBindingFn={keyBindingFn}
                handleBeforeInput={handleBeforeInput}
                handlePastedText={handlePastedText}
            />
            {!readOnly && (
                <>
                    <VariableSelection
                        ref={variableOptionsRef}
                        options={options}
                        visible={variableSelectionOpen}
                        portalRef={popoverPortal}
                        onVisibleChange={onVisibleChange}
                        onSelectVariable={onSelectVariable}
                    />
                    <LinkDialog
                        linkRef={linkButtonRef.current}
                        showArrow={false}
                        isOpen={linkDialogOpen}
                        text={linkData?.text ?? ''}
                        url={linkData?.url ?? ''}
                        onSave={onSaveLink}
                        onVisibleChange={onLinkDialogVisibilityChange}
                    />
                </>
            )}
        </div>
    );
};

const RichEditor = (props: RichEditorProps) => {
    return (
        <EditorContextProviders>
            <RichEditorCore {...props} />
        </EditorContextProviders>
    );
};

export type { RichEditorVariable as Variable } from './types';
export { convertMarkdownToDraft } from './transforms/convertMarkdownToDraft';
export { convertDraftToHTML } from './transforms/convertDraftToHTML';
export default RichEditor;
